import { HttpClient } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { appConfigService } from '@app/config';
import {
  ActionResult,
  AuthRequestOptions,
  Item,
  ServerItemAction
} from '@ntap/core';
import { ConfirmationService } from 'primeng/api';
import { EMPTY, first, Observable, Subject, switchMap } from 'rxjs';

interface KonverzijaEstimateResponse {
  fileCount: number;
  estimatedSeconds: number;
}

@Component({
  templateUrl: './konvertiraj-pojavni-oblik-action.component.html'
})
export class KonvertirajPojavniOblikActionComponent extends ServerItemAction {
  key = 'konvertirajPojavniOblik';

  private http = inject(HttpClient);
  private config = inject(appConfigService);
  private confirmationService = inject(ConfirmationService);

  private static readonly POJAVNI_OBLIK_ID_KEY = 'en~pojavni_oblik~id';
  private static readonly TARGET_FORMAT_KEY =
    'en~pojavni_oblik~vr_format_konverzije';

  public override doExecuteWithItem(item: Item): Observable<ActionResult> {
    const itemAny = item as Record<string, unknown>;
    const pojavniOblikId =
      itemAny[KonvertirajPojavniOblikActionComponent.POJAVNI_OBLIK_ID_KEY] ??
      itemAny['id'];
    const targetFormat =
      itemAny[KonvertirajPojavniOblikActionComponent.TARGET_FORMAT_KEY];

    const url = `${this.config.apiHost}konverzija-pojavnog-oblika/estimate`;
    const payload: Record<string, unknown> = {
      [KonvertirajPojavniOblikActionComponent.POJAVNI_OBLIK_ID_KEY]:
        pojavniOblikId,
      [KonvertirajPojavniOblikActionComponent.TARGET_FORMAT_KEY]: targetFormat
    };

    return this.http
      .post<KonverzijaEstimateResponse>(url, payload, new AuthRequestOptions())
      .pipe(
        switchMap(({ fileCount, estimatedSeconds }) => {
          const hhmm = this.formatHhMm(estimatedSeconds);
          const message =
            `Potvrdite izvođenje akcije konverzije ${fileCount} datoteka. ` +
            `Procijenjeno vrijeme trajanja je ${hhmm}.`;
          return this.askConfirm(message).pipe(
            switchMap((confirmed) =>
              confirmed ? super.doExecuteWithItem(item) : EMPTY
            )
          );
        })
      );
  }

  private askConfirm(message: string): Observable<boolean> {
    const result$ = new Subject<boolean>();
    this.confirmationService.confirm({
      key: this.key,
      message,
      accept: () => {
        result$.next(true);
        result$.complete();
      },
      reject: () => {
        result$.next(false);
        result$.complete();
      }
    });
    return result$.pipe(first());
  }

  private formatHhMm(totalSeconds: number): string {
    const totalMinutes = Math.ceil((totalSeconds ?? 0) / 60);
    const hh = Math.floor(totalMinutes / 60)
      .toString()
      .padStart(2, '0');
    const mm = (totalMinutes % 60).toString().padStart(2, '0');
    return `${hh} h ${mm} min`;
  }
}
